// After a bulk import that returned per-row errors, generate and download
// a CSV containing only the rows that failed, plus an `_error` column with
// the server-side error message. The user can fix the cells and re-import
// the same file — every backend /import endpoint here is upsert-keyed, so
// re-importing a corrected version of the same failures is safe.
//
// Triggered automatically by the axios response interceptor in
// `services/api.js` for any POST to /import or /bulk-import whose response
// payload contains an `errors` array. Per-view code doesn't need to know.
import Papa from 'papaparse';


function _extractOriginalFile(config) {
  // `config.data` for our import calls is a FormData instance. The Vue views
  // always append the file under the key `file`. If anything else is found
  // (or the body is JSON), bail out — we'd rather not download nothing.
  const data = config && config.data;
  if (!data) return null;
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    const f = data.get('file');
    return (f && typeof f.text === 'function') ? f : null;
  }
  return null;
}


function _normaliseErrors(payload) {
  // Backends return one of two shapes for the per-row error list:
  //   1. [{row: N, message: "..."}]                  — most P1 handlers
  //   2. ["Row N: message", ...]                     — legacy string form
  // Normalise to [{row: N, message: "..."}] and drop entries we can't parse.
  const raw = (payload && (payload.errors || payload.errors_sample)) || [];
  if (!Array.isArray(raw)) return [];
  return raw.map(e => {
    if (e && typeof e === 'object' && Number.isFinite(e.row) && typeof e.message === 'string') {
      return { row: e.row, message: e.message };
    }
    if (typeof e === 'string') {
      const m = e.match(/^Row\s+(\d+):\s*(.+)$/i);
      if (m) return { row: parseInt(m[1], 10), message: m[2] };
    }
    return null;
  }).filter(Boolean);
}


function _detectFilenameRoot(url) {
  // Pull a friendly slug out of the import URL for the download filename.
  // /api/v1/wildlife-mortality/import → wildlife-mortality_failed
  const m = String(url || '').match(/\/api\/v\d+\/([^/?#]+)/);
  return (m ? m[1] : 'import') + '_failed';
}


/**
 * Inspect a completed import response. If the server reported any per-row
 * errors AND the user uploaded a CSV (we can't read .xlsx in the browser
 * without extra deps), parse the original CSV, slice it to just the failed
 * rows, prepend `_row` and `_error` columns, and trigger a download.
 *
 * @param {object} response  axios response object (response.config.data must
 *                           be the original FormData).
 * @returns {Promise<boolean>}  true if a download was triggered.
 */
export async function autoDownloadFailedRows(response) {
  try {
    if (!response || !response.config) return false;
    const payload = response.data || {};
    const errors = _normaliseErrors(payload);
    if (errors.length === 0) return false;

    const file = _extractOriginalFile(response.config);
    if (!file) return false;
    if (!/\.csv$/i.test(file.name || '')) {
      // Excel uploads would need a SheetJS dependency to re-parse here.
      // Skip silently — the SweetAlert summary still shows the error count
      // and a sample of messages, so the user isn't left in the dark.
      console.info('[failed-rows] skipping auto-download: original upload was not CSV');
      return false;
    }

    const text = await file.text();
    const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
    if (!parsed.data || parsed.data.length === 0) return false;

    // Map row -> message. Row numbers are 1-based from the original file
    // (header = row 1, first data row = row 2).
    const errByRow = new Map(errors.map(e => [e.row, e.message]));

    const failedRows = parsed.data
      .map((row, idx) => {
        const rowNum = idx + 2;
        if (!errByRow.has(rowNum)) return null;
        return { _row: rowNum, _error: errByRow.get(rowNum), ...row };
      })
      .filter(Boolean);

    if (failedRows.length === 0) return false;

    const csv = Papa.unparse(failedRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
    const url = response.config.url || '';
    const name = `${_detectFilenameRoot(url)}_${ts}.csv`;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return true;
  } catch (e) {
    // Diagnostic only — never block the success path of the import response.
    console.error('[failed-rows] auto-download failed:', e);
    return false;
  }
}
